import { MacroTelemetrySanitizer as Sanitizer } from './macroTelemetrySanitizer';

export interface MacroSentryLog {
  level?: string; message?: unknown; body?: unknown; attributes?: Record<string, unknown>;
  [key: string]: unknown;
}
export interface MacroSentryEvent {
  message?: unknown; tags?: Record<string, unknown>; extra?: Record<string, unknown>;
  [key: string]: unknown;
}
export interface MacroSentryBreadcrumb {
  category?: string; message?: string; data?: Record<string, unknown>;
  [key: string]: unknown;
}
const envelope = { telemetrySchema: 'macro-observability-v2', subsystem: 'macro' };
const label = 'macro diagnostic';

function exceptionMessages(exception: unknown): unknown {
  if (!exception || typeof exception !== 'object') return exception;
  const values = (exception as { values?: unknown }).values;
  if (!Array.isArray(values)) return undefined;
  // Stack frames are structural diagnostics; only exception text identifies a macro.
  return values.slice(0, 64).map(value => ({ type: value?.type, value: value?.value }));
}

export function normalizeMacroSentryLog<T extends MacroSentryLog>(log: T): T {
  if (!Sanitizer.recognizes(log)) return log;
  return { ...log, message: log.message === undefined ? undefined : label,
    body: log.body === undefined ? undefined : label,
    attributes: { ...Sanitizer.sanitize(log.attributes), ...envelope } };
}

export function normalizeMacroSentryBreadcrumb<T extends MacroSentryBreadcrumb>(breadcrumb: T): T {
  if (!Sanitizer.recognizes(breadcrumb)) return breadcrumb;
  return { ...breadcrumb, message: label, data: { ...Sanitizer.sanitize(breadcrumb.data), ...envelope } };
}

/** Mutates events because application hooks rely on in-place normalization. */
export function normalizeMacroSentryEvent<T extends MacroSentryEvent>(event: T): T {
  const macro = Sanitizer.recognizes({ message: event.message, tags: event.tags, extra: event.extra,
    contexts: event['contexts'], exception: exceptionMessages(event['exception']), request: event['request'], logentry: event['logentry'] });
  const mutable: MacroSentryEvent = event;
  if (Array.isArray(event['breadcrumbs'])) {
    mutable['breadcrumbs'] = event['breadcrumbs'].map(crumb => normalizeMacroSentryBreadcrumb(crumb));
  }
  if (!macro) return event;
  event.message = label;
  event.tags = { ...Sanitizer.sanitize(event.tags), ...envelope };
  if (event.extra) event.extra = Sanitizer.sanitize(event.extra);
  if (event['contexts']) mutable['contexts'] = {};
  if (event['request']) mutable['request'] = {};
  if (event['logentry']) mutable['logentry'] = { message: label };
  if (event['exception']) mutable['exception'] = { values: [{ type: 'MacroError', value: label }] };
  return event;
}
